import { useInfiniteQuery } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError, HTTPErrors } from "../../errors";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import { toQueryString } from "../../helpers/TimeHelper";
import ApiResponse from "../../interfaces/ApiResponse";
import StudentAnalyticsTableInterface from "../../interfaces/StudentAnalyticsTableInterface";
import { AnalyticsFilter } from "../filters/useAnalyticsFilter";

interface UseStudentAnalyticsTableOptions {
  type: "time" | "amount";
  studentId: string;
  analyticsFilter: AnalyticsFilter;
  enabled?: boolean;
}

export const useFetchStudentStudyRecordAnalytics = ({
  type,
  studentId,
  analyticsFilter,
  enabled = true,
}: UseStudentAnalyticsTableOptions) => {
  const path =
    `/api/v1/students/{student_id}/analytics/${coerceType(type)}` as const;

  const query = {
    initial_date: analyticsFilter.endDate
      ? toQueryString(analyticsFilter.endDate)
      : undefined,
    term: analyticsFilter?.term,
    order: analyticsFilter?.order,
    order_dir: analyticsFilter?.orderDir,
  };

  const result = useInfiniteQuery<
    ApiResponse<StudentAnalyticsTableInterface>,
    HTTPErrors
  >({
    queryKey: [path, type, studentId, query],
    queryFn: async ({ pageParam }) => {
      const { response, data } = await boronClient.GET(path, {
        params: {
          path: {
            student_id: studentId,
          },
          query: {
            ...query,
            page: pageParam,
          },
        },
      });

      if (response.ok && data) {
        const analyticsData =
          "timeAnalyticsTable" in data
            ? data.timeAnalyticsTable
            : data.amountAnalyticsTable;

        return analyticsData;
      }

      throw await createError(response);
    },
    initialPageParam: 1,
    getNextPageParam,
    enabled,
  });

  const learningMaterials =
    result.data?.pages.flatMap((page) => page.data.learningMaterials) ?? [];
  const columns = result.data?.pages[0]?.data.columns ?? [];
  const totalScore = result.data?.pages[0]?.data.totalScore ?? 0;
  const term = result.data?.pages[0]?.data.term ?? "daily";

  const analyticsTable =
    result.data?.pages[0] == null
      ? undefined
      : {
          learningMaterials,
          columns,
          totalScore,
          term,
        };

  return {
    ...result,
    analyticsTable,
  };
};

const coerceType = (type: "time" | "amount") => {
  return type === "time" ? "time" : "amount";
};
